/**
 * Supabase client singleton
 *
 * This module provides a typed Supabase client configured for the application.
 * Use this client for all database operations.
 */

import { createClient, type SupabaseClient, type WebSocketLikeConstructor } from '@supabase/supabase-js'
import { watchPasswordRecovery } from '@/lib/passwordRecoveryRedirect'
import { guardSupabaseFetch } from '@/lib/supabaseRequestGuard'
import type { Database } from '@/types/supabase'
import { monitoredSupabaseFetch } from './supabaseFetchMonitor'

let supabaseClient: SupabaseClient<Database> | null = null

// supabase-js builds its Realtime client inside createClient and throws there when the browser has no
// WebSocket (hardened builds, some crawlers), which took auth, REST and storage down with it. Martini
// never opens a Supabase Realtime channel, so in those browsers Realtime gets a transport that fails
// only if something actually connects.
class UnavailableWebSocket {
  constructor() {
    throw new Error('WebSocket is not available in this runtime')
  }
}

function createSupabaseClient(): SupabaseClient<Database> {
  const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL
  const supabaseAnonKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY

  if (!supabaseUrl || !supabaseAnonKey) {
    throw new Error(
      'Missing Supabase environment variables. Set NEXT_PUBLIC_SUPABASE_URL and NEXT_PUBLIC_SUPABASE_ANON_KEY',
    )
  }

  const client = createClient<Database>(supabaseUrl, supabaseAnonKey, {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true, // Required for OAuth redirects
    },
    // Every request the client makes (REST, auth, storage) passes through both:
    // the monitor reports failures even when the calling code swallows them, and
    // the guard reports reads that near the URL limit or come back trimmed to the row cap.
    global: { fetch: guardSupabaseFetch(() => monitoredSupabaseFetch) },
    ...(typeof globalThis.WebSocket === 'undefined' && {
      realtime: { transport: UnavailableWebSocket as unknown as WebSocketLikeConstructor },
    }),
  })
  // Before anything else awaits the client: the recovery sign-in is announced once, as the link is read.
  if (typeof window !== 'undefined') watchPasswordRecovery(client.auth.onAuthStateChange.bind(client.auth))
  return client
}

export function getSupabaseClient(): SupabaseClient<Database> {
  if (!supabaseClient) {
    supabaseClient = createSupabaseClient()
  }

  return supabaseClient
}

export const supabase = new Proxy({} as SupabaseClient<Database>, {
  get(_target, prop) {
    const client = getSupabaseClient()
    const value = client[prop as keyof SupabaseClient<Database>]

    if (typeof value === 'function') {
      return value.bind(client)
    }

    return value
  },
})
